import React, { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { AvatarChip, AVATAR_EMOJIS, AVATAR_COLORS, DEFAULT_AVATAR } from '../components/Avatar';
import DrawingCanvas from '../components/DrawingCanvas';
import { ArrowLeft, Save, Shuffle, Palette, Smile } from 'lucide-react';

export default function AvatarEditor() {
  const { profile, setScreen, updateAvatar, playerName } = useGame();
  const existing = profile?.avatar || DEFAULT_AVATAR;
  const titles = profile?.titles || [];
  const [emoji, setEmoji] = useState(existing.emoji || DEFAULT_AVATAR.emoji);
  const [color, setColor] = useState(existing.color || DEFAULT_AVATAR.color);
  const [title, setTitle] = useState(existing.title || null);
  const [drawing, setDrawing] = useState(existing.drawing || null); // dataURL or null
  const [mode, setMode] = useState(existing.drawing ? 'draw' : 'emoji'); // 'emoji' | 'draw'
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile?.avatar) {
      setEmoji(profile.avatar.emoji || DEFAULT_AVATAR.emoji);
      setColor(profile.avatar.color || DEFAULT_AVATAR.color);
      setTitle(profile.avatar.title || null);
      setDrawing(profile.avatar.drawing || null);
    }
  }, [profile]);

  // v2.0 QoL: ESC to leave editor
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setScreen('menu'); }
      else if (e.key === 'r' || e.key === 'R') { doRandomize(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRandomize = () => {
    const e = AVATAR_EMOJIS[Math.floor(Math.random() * AVATAR_EMOJIS.length)];
    const c = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)].key;
    setEmoji(e);
    setColor(c);
  };

  const doSave = async () => {
    // If in drawing mode we persist the drawing; otherwise clear it so emoji shows.
    const p = await updateAvatar({
      emoji,
      color,
      title,
      drawing: mode === 'draw' ? drawing : null,
    });
    if (p) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1400);
    }
  };

  const handleDrawingSave = (dataUrl) => {
    setDrawing(dataUrl);
    setMode('draw');
  };
  const handleDrawingClear = () => {
    setDrawing(null);
    setMode('emoji');
  };

  return (
    <div className="min-h-screen w-full px-6 py-6" data-testid="avatar-editor-screen">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={() => setScreen('menu')} data-testid="avatar-editor-back">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[color:var(--neon)]">CUSTOMIZE AVATAR</h1>
        <div className="w-24"/>
      </div>

      {/* Live preview */}
      <div className="mt-6 flex flex-col items-center gap-3">
        <AvatarChip
          avatar={{ emoji, color, title, drawing: mode === 'draw' ? drawing : null }}
          size={160}
          testid="avatar-preview"
        />
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">CURRENT</div>
          <div className="font-display font-black text-2xl uppercase" style={{ color: 'var(--neon)' }}>{playerName || 'ANON_GOON'}</div>
          {title && (
            <div className="text-xs uppercase font-mono text-[color:var(--pink)] mt-1" data-testid="avatar-preview-title">
              THE {title}
            </div>
          )}
        </div>
      </div>

      {/* Mode tabs */}
      <div className="mt-6 max-w-3xl mx-auto flex justify-center gap-2" data-testid="avatar-mode-tabs">
        <button
          className={`brutal-btn text-sm px-4 py-2 ${mode === 'emoji' ? 'active' : ''}`}
          onClick={() => setMode('emoji')}
          data-testid="avatar-mode-emoji"
          data-active={mode === 'emoji' ? 'true' : 'false'}
          style={mode === 'emoji' ? { borderColor: 'var(--neon)', boxShadow: '4px 4px 0 var(--neon)' } : {}}
        >
          <Smile size={14}/> EMOJI
        </button>
        <button
          className={`brutal-btn text-sm px-4 py-2 ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => setMode('draw')}
          data-testid="avatar-mode-draw"
          data-active={mode === 'draw' ? 'true' : 'false'}
          style={mode === 'draw' ? { borderColor: 'var(--pink)', boxShadow: '4px 4px 0 var(--pink)', color: 'var(--pink)' } : {}}
        >
          <Palette size={14}/> DRAW
        </button>
      </div>

      {mode === 'draw' ? (
        <div className="mt-6 max-w-3xl mx-auto" data-testid="avatar-drawing-mode">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2 text-center">
            DRAW YOUR OWN FACE — click USE DRAWING to save the canvas
          </div>
          <DrawingCanvas
            initialDataUrl={drawing}
            onSave={handleDrawingSave}
            onClear={handleDrawingClear}
          />
        </div>
      ) : (
        <>
          {/* Emoji grid */}
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">FACE</div>
            <div className="grid grid-cols-10 gap-2" data-testid="avatar-emoji-grid">
              {AVATAR_EMOJIS.map(e => (
                <button
                  key={e}
                  className={`avatar-emoji-cell ${e === emoji ? 'selected' : ''}`}
                  onClick={() => setEmoji(e)}
                  data-testid={`avatar-emoji-${e}`}
                  data-selected={e === emoji ? 'true' : 'false'}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Color palette (used for chip border in both modes) */}
      <div className="mt-6 max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">AURA COLOR (chip border)</div>
        <div className="flex flex-wrap gap-2" data-testid="avatar-color-grid">
          {AVATAR_COLORS.map(c => (
            <button
              key={c.key}
              className={`avatar-color-swatch ${c.key === color ? 'selected' : ''}`}
              onClick={() => setColor(c.key)}
              data-testid={`avatar-color-${c.key}`}
              data-selected={c.key === color ? 'true' : 'false'}
              style={{ borderColor: c.hex, boxShadow: c.key === color ? `4px 4px 0 ${c.hex}` : 'none' }}
              title={c.name}
            >
              <span className="avatar-color-dot" style={{ background: c.hex }} />
              <span className="avatar-color-name">{c.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Titles — only earned ones */}
      <div className="mt-6 max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">TITLE (earned from achievements)</div>
        <div className="flex flex-wrap gap-2" data-testid="avatar-title-list">
          <button
            className={`avatar-title-cell ${title === null ? 'selected' : ''}`}
            onClick={() => setTitle(null)}
            data-testid="avatar-title-none"
          >— NONE —</button>
          {titles.length === 0 && (
            <div className="text-[color:var(--text-dim)] text-xs italic px-3 py-2">
              Earn achievements to unlock titles.
            </div>
          )}
          {titles.map(t => (
            <button
              key={t}
              className={`avatar-title-cell ${t === title ? 'selected' : ''}`}
              onClick={() => setTitle(t)}
              data-testid={`avatar-title-${t.replace(/\s+/g,'_')}`}
            >THE {t}</button>
          ))}
        </div>
      </div>

      <div className="mt-8 max-w-3xl mx-auto flex justify-center gap-3 flex-wrap">
        <button
          className="brutal-btn text-sm px-5 py-3"
          onClick={doRandomize}
          data-testid="avatar-randomize"
          title="Shuffle random emoji + color (R)"
        >
          <Shuffle size={16}/> RANDOMIZE <kbd className="ml-2 px-1.5 py-0.5 text-[10px] bg-[#111] border border-[color:var(--neon)] rounded">R</kbd>
        </button>
        <button
          className="brutal-btn text-lg px-8 py-3"
          onClick={doSave}
          data-testid="avatar-save"
        >
          <Save size={16}/> {saved ? 'SAVED!' : 'SAVE AVATAR'}
        </button>
      </div>
      <div className="mt-4 text-center text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">
        ESC to go back · R to randomize
      </div>
    </div>
  );
}
